/**
 * Run something after the pointer press in progress ends (D-R17-04). A field validated on blur shows its message the
 * moment it loses focus — and a press on a button below it takes the focus away on mousedown. The message then pushes the
 * button down before mouseup, the press ends off the button, and the click is lost: the first Save did nothing visible.
 * Holding the blur's message back until the press has ended (and its click has fired) keeps the layout still for the
 * whole press. Nothing waits when no press is in progress (a Tab away shows the message at once).
 */
let pressed = false;
let installed = false;
const waiting = new Set<() => void>();

function release() {
  pressed = false;
  if (waiting.size === 0) return;
  // The click fires right after pointerup, in the same task; run once it has.
  setTimeout(() => {
    const due = [...waiting];
    waiting.clear();
    for (const run of due) run();
  }, 0);
}

/** Listens on the document (capture, so no handler can hide the press). Idempotent; a no-op without a document. */
export function installPointerRelease(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("pointerdown", () => {
    pressed = true;
  }, true);
  document.addEventListener("pointerup", release, true);
  document.addEventListener("pointercancel", release, true);
  // A press released outside the window sends no pointerup here: never wait past the next focus of the page.
  window.addEventListener("blur", release);
}

/** `run` now, or once the pointer press in progress has ended and its click has fired. */
export function afterPointerRelease(run: () => void): void {
  installPointerRelease();
  if (pressed) waiting.add(run);
  else run();
}
